import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBullhorn } from '@fortawesome/pro-duotone-svg-icons';
import { NewsDTO } from '@bludata/fc-aggregator';
import { getReleasesClient } from '@focus.cloud/src/login/api/ClientServicesCloud';
import DashboardNewsItem from '@focus.cloud/src/dashboard/components/news/DashboardNewsItem';

const PAGE_SIZE = 10;

interface Props {
  onClose: () => void;
}

function DialogFocusWebVersions(props: Props) {
  const { onClose } = props;
  const [releasesList, setReleasesList] = useState<NewsDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const pageIndexRef = useRef(0);
  const fetchingRef = useRef(false);
  const abortControllerRef = useRef<AbortController>();

  const fetchReleases = useCallback((pageIndex: number) => {
    const client = getReleasesClient();
    if (!client) return;

    // Cancella richiesta precedente per evitare race condition
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const isFirstPage = pageIndex === 1;
    if (isFirstPage) setLoading(true);
    else setLoadingMore(true);
    fetchingRef.current = true;

    client
      .getAll({ index: pageIndex, size: PAGE_SIZE })
      .then((result) => {
        if (controller.signal.aborted) return;
        const items = result?.items ?? [];
        const paging = result?.paging;
        const totalPages = paging?.virtualPages ?? 0;

        setReleasesList((prev) => (isFirstPage ? items : [...prev, ...items]));
        pageIndexRef.current = pageIndex;
        setHasMore(pageIndex < totalPages);
      })
      .catch((error) => {
        if (controller.signal.aborted || error?.name === 'CanceledError')
          return;
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        if (isFirstPage) setLoading(false);
        else setLoadingMore(false);
        fetchingRef.current = false;
      });
  }, []);

  useEffect(() => {
    fetchReleases(1);
    return () => {
      abortControllerRef.current?.abort();
    };
  }, [fetchReleases]);

  const onScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container || fetchingRef.current || !hasMore) return;

    const { scrollTop, clientHeight, scrollHeight } = container;
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      fetchReleases(pageIndexRef.current + 1);
    }
  }, [hasMore, fetchReleases]);

  return (
    <Dialog open fullWidth maxWidth="md" fullScreen={false}>
      <DialogTitle>
        <Stack flexDirection="row" gap={1} alignItems="center">
          <FontAwesomeIcon icon={faBullhorn} /> Novità aggiornamenti
        </Stack>
      </DialogTitle>
      <DialogContent
        ref={containerRef}
        onScroll={onScroll}
        sx={{ maxHeight: 500 }}
      >
        {loading ? (
          <Stack flex={1} justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
          </Stack>
        ) : (
          <Stack flexDirection="column" spacing={1}>
            {releasesList.map((release) => (
              <DashboardNewsItem
                key={release.id}
                news={release}
                icon={faBullhorn}
              />
            ))}
            {loadingMore && (
              <Stack alignItems="center" py={1}>
                <CircularProgress size={24} />
              </Stack>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Chiudi
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DialogFocusWebVersions;
